import dayjs from 'dayjs';
import userflow from 'userflow.js';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Loading from 'components/global/Loading';

import useAppTracking from 'hooks/useAppTracking';
import useChangeLanguage from 'hooks/useChangeLanguage';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { setDarkMode } from 'stores/layoutSlice';

import 'utils/initYup';

import { Breakpoints } from './const.styles';
import { languages } from './localization';

const AppTheme = ({ children }) => {
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	useEffect(() => {
		const fontSize = Number(localStorage.getItem('layoutScale'));
		if (fontSize) {
			if (isMobile) {
				document.getElementsByTagName('html')[0].style.fontSize = `${fontSize + 2}px`;
			} else {
				document.getElementsByTagName('html')[0].style.fontSize = `calc(${fontSize}px + 0.12vw)`;
			}
		}

		userflow.setResourceCenterLauncherHidden(true);

		if (userflow.isIdentified()) {
			userflow.updateUser({
				isMobile
			});
		}
	}, [isMobile]);

	const isUserInitialized = useSelector((state) => state.user.initialized);
	const dispatch = useDispatch();
	const { loading: layoutLoading, darkMode, themeName } = useSelector((state) => state.layout);
	const colorSchemaMediaQuery = useMediaQuery('(prefers-color-scheme: dark)');

	useEffect(() => {
		let isDarkMode;

		if (themeName === 'systemTheme') {
			isDarkMode = colorSchemaMediaQuery;
		} else {
			isDarkMode = themeName === 'darkTheme';
		}

		if (isDarkMode !== darkMode) {
			dispatch(setDarkMode(isDarkMode));
		}

		if (isDarkMode && isUserInitialized) {
			if (!document.body.classList.contains('dark-theme-variable')) {
				document.body.classList.add('dark-theme-variable');
			}

			// Flatpickr dark theme workaround
			const flatpickrLink = document.createElement('link');
			flatpickrLink.id = 'FlatpickrDarkTheme';
			flatpickrLink.href = 'https://npmcdn.com/flatpickr/dist/themes/dark.css';
			flatpickrLink.rel = 'stylesheet';
			flatpickrLink.type = 'text/css';
			document.head.appendChild(flatpickrLink);

			// TinyMCE dark theme workaround
			const tinyMCELightThemeLink = document.getElementById('TinyMCELightTheme');
			if (tinyMCELightThemeLink) {
				tinyMCELightThemeLink.remove();
			}

			const tinymceLink = document.createElement('link');
			tinymceLink.id = 'TinyMCEDarkTheme';
			tinymceLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.0/skins/ui/oxide-dark/skin.min.css';
			tinymceLink.rel = 'stylesheet';
			tinymceLink.type = 'text/css';
			document.head.appendChild(tinymceLink);
		} else {
			if (document.body.classList.contains('dark-theme-variable')) {
				document.body.classList.remove('dark-theme-variable');
			}

			const flatpickrLink = document.getElementById('FlatpickrDarkTheme');
			if (flatpickrLink) {
				flatpickrLink.remove();
			}

			const tinymceDarkThemeLink = document.getElementById('TinyMCEDarkTheme');
			if (tinymceDarkThemeLink) {
				tinymceDarkThemeLink.remove();
			}

			// TinyMCE light theme workaround
			const tinymceLink = document.createElement('link');
			tinymceLink.id = 'TinyMCELightTheme';
			tinymceLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.0/skins/ui/oxide/skin.min.css';
			tinymceLink.rel = 'stylesheet';
			tinymceLink.type = 'text/css';
			document.head.appendChild(tinymceLink);
		}
	}, [themeName, colorSchemaMediaQuery, isUserInitialized]);

	const [loading, setLoading] = useState(true);
	const { LanguageCode, TimeZone } = useLocale();
	const changeLanguage = useChangeLanguage();

	useEffect(() => {
		if (LanguageCode) {
			changeLanguage(LanguageCode).then(async () => {
				setLoading(false);
			});
		}
	}, []);

	useEffect(() => {
		if (isUserInitialized && isMobile) {
			userflow.closeResourceCenter?.();
		}
	}, [isMobile, isUserInitialized]);

	useEffect(() => {
		dayjs.tz.setDefault(TimeZone);
	}, [TimeZone]);

	const { init } = useAppTracking();

	useEffect(() => {
		init();
	}, []);

	const [electronLang, setElectronLang] = useState(null);

	useEffect(() => {
		if (window.electronAPI) {
			window.electronAPI.onLanguageChange((lang) => {
				setElectronLang(lang);
			});
		}
	}, []);

	useEffect(() => {
		if (window.electronAPI && electronLang) {
			const langObj = languages.find((language) => language.languageCode === electronLang);
			if (langObj) {
				changeLanguage(langObj.code);
			}
		}
	}, [changeLanguage, electronLang]);

	return loading ? (
		<Loading />
	) : (
		<>
			{children}
			{layoutLoading && <Loading />}
		</>
	);
};

export default AppTheme;
